import type {
  ApiResponse,
  AuthResponse,
  AvailabilitySearchRequest,
  AvailabilitySearchResponse,
  BookingConfirmation,
  BookingIdentityRequest,
  BookingIntentRequest,
  BookingIntentResponse,
  BookingPaymentSession,
  Faq,
  FaqInput,
  FeaturedRoomsResponse,
  GalleryItem,
  GalleryItemInput,
  MemberBookingsResponse,
  MemberPasswordChange,
  MemberProfile,
  MemberProfileUpdate,
  MemberRegisterRequest,
  Membership,
  PublicUser,
  RefreshResponse,
  SecretsEvent,
  SecretsGalleryImage,
  SecretsNewsArticle,
  Slide,
  SlideInput,
} from '@secretsoverflow/types';

export const API_URL: string = import.meta.env.VITE_API_URL ?? 'http://localhost:4000';

export class ApiClientError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: Record<string, string[]>;

  constructor(status: number, code: string, message: string, details?: Record<string, string[]>) {
    super(message);
    this.name = 'ApiClientError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

/** In-memory access token (never persisted to storage). */
let accessToken: string | null = null;

export const setAccessToken = (token: string | null): void => {
  accessToken = token;
};
export const getAccessToken = (): string | null => accessToken;

interface RequestOptions {
  method?: string;
  body?: unknown;
  /** Send the httpOnly refresh cookie (auth endpoints). */
  auth?: boolean;
  /** Attach the bearer access token. */
  bearer?: boolean;
  signal?: AbortSignal;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = {};
  if (options.body !== undefined) headers['Content-Type'] = 'application/json';
  if (options.bearer && accessToken) headers.Authorization = `Bearer ${accessToken}`;

  const res = await fetch(`${API_URL}${path}`, {
    method: options.method ?? 'GET',
    headers,
    body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
    credentials: options.auth ? 'include' : 'same-origin',
    signal: options.signal,
  });

  const payload = (await res.json().catch(() => null)) as ApiResponse<T> | null;

  if (!payload) {
    throw new ApiClientError(res.status, 'network_error', 'Empty or invalid response');
  }
  if (!payload.ok) {
    throw new ApiClientError(
      res.status,
      payload.error.code,
      payload.error.message,
      payload.error.details,
    );
  }
  return payload.data;
}

/** Build the availability query string (origin is always Secrets Hideaway). */
function availabilityQuery(params: AvailabilitySearchRequest): string {
  return new URLSearchParams({
    radiusKm: String(params.radiusKm),
    checkIn: params.checkIn,
    checkOut: params.checkOut,
    guests: String(params.guests),
  }).toString();
}

/** POST a file as the raw request body (the avatar endpoint takes one image). */
async function requestRaw<T>(path: string, file: File): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': file.type };
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;

  const res = await fetch(`${API_URL}${path}`, {
    method: 'POST',
    headers,
    body: file,
    credentials: 'same-origin',
  });

  const payload = (await res.json().catch(() => null)) as ApiResponse<T> | null;
  if (!payload) throw new ApiClientError(res.status, 'network_error', 'Empty or invalid response');
  if (!payload.ok) {
    throw new ApiClientError(
      res.status,
      payload.error.code,
      payload.error.message,
      payload.error.details,
    );
  }
  return payload.data;
}

export const api = {
  availability: {
    search: (params: AvailabilitySearchRequest, signal?: AbortSignal) =>
      request<AvailabilitySearchResponse>(`/v1/availability?${availabilityQuery(params)}`, {
        signal,
      }),
    // The nearby-hotel showcase, cached server-side on a slow cadence.
    featured: (signal?: AbortSignal) =>
      request<FeaturedRoomsResponse>('/v1/availability/featured', { signal }),
  },
  // A booking is recorded when a room is picked, before anyone has identified
  // themselves, so an abandoned checkout still leaves us the search behind it.
  // Both calls send the bearer token when there is one, which is what links the
  // record to a signed-in member.
  bookings: {
    start: (body: BookingIntentRequest) =>
      request<BookingIntentResponse>('/v1/bookings', { method: 'POST', body, bearer: true }),
    identify: (id: string, body: BookingIdentityRequest) =>
      request<{ recorded: boolean }>(`/v1/bookings/${id}/identity`, {
        method: 'POST',
        body,
        bearer: true,
      }),
    // Holds the room and returns what the vendor's payment SDK needs. The
    // client secret is used immediately and never stored.
    payment: (id: string) =>
      request<BookingPaymentSession>(`/v1/bookings/${id}/payment`, {
        method: 'POST',
        bearer: true,
      }),
    // Places the reservation. Idempotent server-side, so a double submit or a
    // reload returns the existing confirmation rather than booking twice.
    confirm: (id: string) =>
      request<BookingConfirmation>(`/v1/bookings/${id}/confirm`, {
        method: 'POST',
        bearer: true,
      }),
  },
  // Members authenticate against the Secrets API via our backend, which then
  // issues one of our sessions. Refresh and logout are shared with `auth`.
  member: {
    login: (body: { email: string; password: string }) =>
      request<{ member: MemberProfile; accessToken: string }>('/v1/member/login', {
        method: 'POST',
        body,
        auth: true,
      }),
    register: (body: MemberRegisterRequest) =>
      request<{ member: MemberProfile; accessToken: string | null; pendingReason: string | null }>(
        '/v1/member/register',
        { method: 'POST', body, auth: true },
      ),
    me: () => request<MemberProfile>('/v1/member/me', { bearer: true }),
    bookings: (signal?: AbortSignal) =>
      request<MemberBookingsResponse>('/v1/member/me/bookings', { bearer: true, signal }),
    update: (body: MemberProfileUpdate) =>
      request<MemberProfile>('/v1/member/me', { method: 'PUT', body, bearer: true }),
    changePassword: (body: MemberPasswordChange) =>
      request<{ changed: boolean }>('/v1/member/me/password', {
        method: 'PUT',
        body,
        bearer: true,
      }),
    uploadAvatar: (file: File) => requestRaw<MemberProfile>('/v1/member/me/avatar', file),
    // Both public: somebody who has forgotten their password has no session.
    requestPasswordReset: (email: string) =>
      request<{ sent: boolean }>('/v1/member/password-reset', {
        method: 'POST',
        body: { email },
        auth: true,
      }),
    confirmPasswordReset: (token: string, password: string) =>
      request<{ reset: boolean }>('/v1/member/password-reset/confirm', {
        method: 'POST',
        body: { token, password },
        auth: true,
      }),
  },
  auth: {
    register: (body: { email: string; password: string; firstName?: string; lastName?: string }) =>
      request<AuthResponse>('/v1/auth/register', { method: 'POST', body, auth: true }),
    login: (body: { email: string; password: string }) =>
      request<AuthResponse>('/v1/auth/login', { method: 'POST', body, auth: true }),
    refresh: () => request<RefreshResponse>('/v1/auth/refresh', { method: 'POST', auth: true }),
    logout: () => request<{ loggedOut: boolean }>('/v1/auth/logout', { method: 'POST', auth: true }),
    me: () => request<PublicUser>('/v1/auth/me', { bearer: true }),
  },
  membership: {
    me: () => request<Membership>('/v1/membership/me', { bearer: true }),
  },
  admin: {
    overview: () =>
      request<{
        members: { total: number; admins: number; members: number };
        memberships: { total: number; active: number };
        bookings: {
          total: number;
          confirmed: number;
          started: number;
          /** Paid, then refused by the vendor: money held against no room. */
          failed: number;
          failedValueCents: number;
          revenueCents: number;
          guests: number;
          members: number;
          withIdentity: number;
        };
        bookingsByDay: Array<{ day: string; started: number; confirmed: number }>;
        failedBookings: Array<{
          id: string;
          guestEmail: string | null;
          guestName: string;
          hotelName: string;
          checkIn: string;
          totalPriceCents: number;
          currency: string;
          paidAt: string | null;
          failedAt: string | null;
        }>;
        topHotels: Array<{
          hotelName: string;
          city: string | null;
          bookings: number;
          confirmed: number;
          avgNightlyCents: number;
        }>;
        stayDemand: Array<{ date: string; searched: number; booked: number; confirmed: number }>;
        cache: { total: number; fresh: number; stale: number; provider: string };
        searchesByDay: Array<{ day: string; count: number }>;
      }>('/v1/admin/overview', { bearer: true }),
    members: (page = 1, pageSize = 25) =>
      request<{ items: PublicUser[]; total: number; page: number; pageSize: number }>(
        `/v1/admin/members?page=${page}&pageSize=${pageSize}`,
        { bearer: true },
      ),
    availabilityCache: () =>
      request<{
        total: number;
        fresh: number;
        stale: number;
        provider: string;
        recent: Array<{ queryText: string; expiresAt: string; createdAt: string }>;
      }>('/v1/admin/availability-cache', { bearer: true }),
  },
  // Public content reads for the template pages. Slides + FAQs are admin-managed;
  // Gallery, News, and Events come from the SecretsFL API.
  content: {
    slides: () => request<Slide[]>('/v1/content/slides'),
    faqs: () => request<Faq[]>('/v1/content/faqs'),
    gallery: () => request<SecretsGalleryImage[]>('/v1/content/gallery'),
    news: () => request<SecretsNewsArticle[]>('/v1/content/news'),
    newsItem: (id: string) => request<SecretsNewsArticle>(`/v1/content/news/${id}`),
    events: () => request<SecretsEvent[]>('/v1/content/events'),
    eventItem: (id: string) => request<SecretsEvent>(`/v1/content/events/${id}`),
  },
  // Admin CRUD for the managed content collections.
  adminContent: {
    slides: {
      list: () => request<Slide[]>('/v1/admin/content/slides', { bearer: true }),
      create: (body: SlideInput) =>
        request<Slide>('/v1/admin/content/slides', { method: 'POST', body, bearer: true }),
      update: (id: string, body: SlideInput) =>
        request<Slide>(`/v1/admin/content/slides/${id}`, { method: 'PUT', body, bearer: true }),
      remove: (id: string) =>
        request<{ deleted: boolean }>(`/v1/admin/content/slides/${id}`, {
          method: 'DELETE',
          bearer: true,
        }),
    },
    gallery: {
      list: () => request<GalleryItem[]>('/v1/admin/content/gallery', { bearer: true }),
      create: (body: GalleryItemInput) =>
        request<GalleryItem>('/v1/admin/content/gallery', { method: 'POST', body, bearer: true }),
      update: (id: string, body: GalleryItemInput) =>
        request<GalleryItem>(`/v1/admin/content/gallery/${id}`, {
          method: 'PUT',
          body,
          bearer: true,
        }),
      remove: (id: string) =>
        request<{ deleted: boolean }>(`/v1/admin/content/gallery/${id}`, {
          method: 'DELETE',
          bearer: true,
        }),
    },
    faqs: {
      list: () => request<Faq[]>('/v1/admin/content/faqs', { bearer: true }),
      create: (body: FaqInput) =>
        request<Faq>('/v1/admin/content/faqs', { method: 'POST', body, bearer: true }),
      update: (id: string, body: FaqInput) =>
        request<Faq>(`/v1/admin/content/faqs/${id}`, { method: 'PUT', body, bearer: true }),
      remove: (id: string) =>
        request<{ deleted: boolean }>(`/v1/admin/content/faqs/${id}`, {
          method: 'DELETE',
          bearer: true,
        }),
    },
  },
};
